import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PRIVACY_CONTACT_EMAIL } from './SupportPage';

const REQUEST_SUBJECT = 'Delete my StandLogic account';
const REQUEST_BODY =
  'Please delete my StandLogic account and all associated data.\n\n'
  + 'Email address I sign in with (if different from this one): \n';
const REQUEST_MAILTO = `mailto:${PRIVACY_CONTACT_EMAIL}?subject=${encodeURIComponent(REQUEST_SUBJECT)}&body=${encodeURIComponent(REQUEST_BODY)}`;

/**
 * Public account deletion page. Google Play requires a web URL where people
 * can request deletion even if they no longer have the app installed.
 */
function DeleteAccountPage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="support-page">
      <div className="support-card">
        <h3>Delete Your StandLogic Account</h3>
        <p className="support-intro">
          You can delete your StandLogic account and its data at any time. This applies to both the website and the
          StandLogic Android app, which share the same account. If you no longer have the app installed, use Option 2 below.
        </p>

        <div className="support-section">
          <h4>Option 1: Delete it yourself (immediate)</h4>
          <ol className="support-list">
            <li><strong>1.</strong> Sign in to the StandLogic Android app with your Google account.</li>
            <li><strong>2.</strong> Open the menu and choose <strong>Profile</strong>.</li>
            <li><strong>3.</strong> Select <strong>Delete My Account</strong> and confirm.</li>
          </ol>
          <p>Your account and data are deleted right away. This cannot be undone.</p>
          <p>
            If you have a Google Play subscription, deletion requests cancellation of future renewals and ends access
            immediately. It does not issue a refund. Canceling in Google Play without deleting your account keeps Plus
            through the paid period.
          </p>
          {isAuthenticated && (
            <p>
              <Link to="/profile">Go to your Profile to delete your account</Link>
            </p>
          )}
        </div>

        <div className="support-section">
          <h4>Option 2: Ask us to delete it</h4>
          <p>
            If you can't sign in, email <a href={REQUEST_MAILTO}>{PRIVACY_CONTACT_EMAIL}</a> with the subject
            "{REQUEST_SUBJECT}". Send it from the Google email address you use with StandLogic, or include that address
            in the message, so we can find your account. We'll confirm by email and delete the account within 30 days.
          </p>
        </div>

        <div className="support-section">
          <h4>What gets deleted</h4>
          <ul className="support-list">
            <li>Your email address and the link to your Google sign-in</li>
            <li>Your nickname</li>
            <li>Your zipcode and any saved locations and their names</li>
            <li>Your plan level (Free or Plus) and last sign-in date</li>
          </ul>
        </div>

        <div className="support-section">
          <h4>What is kept, and for how long</h4>
          <ul className="support-list">
            <li>
              <strong>Server logs</strong> (such as request times and IP addresses) are kept for up to 90 days for
              security and troubleshooting, then deleted automatically. They are not linked to a profile.
            </li>
            <li>
              <strong>Email correspondence</strong> you send us, including a deletion request, stays in our inbox.
            </li>
            <li>
              <strong>Billing identifiers</strong>, if you subscribed through Google Play (a hashed purchase-token
              reference and your account identifier), are retained for 90 days to handle delayed subscription
              notifications, then scheduled for automatic deletion. AWS may take additional time to remove expired
              records. Google Play retains its own transaction records under its policies.
            </li>
          </ul>
          <p>
            Data saved on your device (your sign-in session and profile copy) is removed when you sign out, clear your
            browser data or uninstall the app. Signing in again later creates a new, empty account.
          </p>
        </div>

        <div className="support-section">
          <h4>Your Google account</h4>
          <p>
            Deleting your StandLogic account doesn't change your Google account. To also remove StandLogic's access from
            Google, visit your <a href="https://myaccount.google.com/connections" target="_blank" rel="noopener noreferrer">Google Account connections</a> page.
          </p>
        </div>

        <div className="support-section">
          <p>
            See our <Link to="/privacy">Privacy Policy</Link> for more about the data we collect.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeleteAccountPage;
